import { supabase } from '../lib/supabase';

// ========== Auth ==========
export async function register(email: string, password: string, role: string, firstName: string, lastName: string) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { data: { role, first_name: firstName, last_name: lastName } },
  });
  if (error) throw new Error(error.message);
  return { success: true, user: data.user };
}

export async function login(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw new Error(error.message);
  return { success: true, user: data.user, session: data.session };
}

export async function getCurrentUser() {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error || !user) return { success: false, user: null };

  const { data: profile } = await supabase.from('profiles').select('*').eq('id', user.id).single();
  return { success: true, user: profile };
}

// ========== Schools ==========
export async function getSchools(filters?: {
  search?: string;
  curriculum?: string;
  ageMin?: number;
  ageMax?: number;
  maxFees?: number;
  availability?: string;
  type?: string;
  lat?: number;
  lon?: number;
  maxDistance?: number;
}) {
  let query = supabase
    .from('school_profiles')
    .select('*')
    .eq('verified', true)
    .eq('active', true);

  if (filters?.search) {
    query = query.or(`name.ilike.%${filters.search}%,city.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
  }
  if (filters?.curriculum) {
    query = query.ilike('curriculum', `%${filters.curriculum}%`);
  }
  if (filters?.ageMin !== undefined) {
    query = query.lte('age_min', filters.ageMin);
  }
  if (filters?.ageMax !== undefined) {
    query = query.gte('age_max', filters.ageMax);
  }
  if (filters?.maxFees !== undefined) {
    query = query.or(`day_fees.lte.${filters.maxFees},boarding_fees.lte.${filters.maxFees}`);
  }
  if (filters?.availability) {
    query = query.eq('availability', filters.availability);
  }
  if (filters?.type) {
    query = query.eq('type', filters.type);
  }

  const { data, error } = await query.order('name');
  if (error) throw new Error(error.message);
  return { success: true, schools: data || [] };
}

export async function getSchool(id: string) {
  const { data, error } = await supabase
    .from('school_profiles')
    .select('*, open_days(*)')
    .eq('id', id)
    .single();
  if (error) throw new Error(error.message);
  return { success: true, school: data };
}

export async function getMySchool() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('school_profiles')
    .select('*')
    .eq('user_id', user.id)
    .single();
  if (error) throw new Error(error.message);
  return { success: true, school: data };
}

export async function updateSchool(id: string, data: any) {
  const { data: updated, error } = await supabase
    .from('school_profiles')
    .update({ ...data, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return { success: true, school: updated };
}

export async function addToShortlist(schoolId: string) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { error } = await supabase
    .from('shortlists')
    .insert({ parent_id: user.id, school_id: schoolId });
  if (error) throw new Error(error.message);
  return { success: true };
}

export async function removeFromShortlist(schoolId: string) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { error } = await supabase
    .from('shortlists')
    .delete()
    .eq('parent_id', user.id)
    .eq('school_id', schoolId);
  if (error) throw new Error(error.message);
  return { success: true };
}

export async function getShortlist() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('shortlists')
    .select('*, school_profiles(*)')
    .eq('parent_id', user.id);
  if (error) throw new Error(error.message);
  return { success: true, shortlist: data || [] };
}

export async function signContract(accepted: boolean) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { error } = await supabase
    .from('school_profiles')
    .update({
      contract_signed: accepted,
      contract_signed_at: accepted ? new Date().toISOString() : null,
      contract_version: accepted ? '1.0' : null,
    })
    .eq('user_id', user.id);
  if (error) throw new Error(error.message);
  return { success: true };
}

// ========== Children ==========
export async function getChildren() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('children')
    .select('*')
    .eq('parent_id', user.id)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return { success: true, children: data || [] };
}

export async function createChild(childData: {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  age: number;
  currentYear?: string;
  interests?: string;
  requirements?: string;
  preferredCurriculum?: string;
  preferredType?: string;
  preferredBoarding?: string;
  notes?: string;
}) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('children')
    .insert({
      parent_id: user.id,
      name: `${childData.firstName} ${childData.lastName}`,
      date_of_birth: childData.dateOfBirth,
      age: childData.age,
      current_year_group: childData.currentYear,
      special_needs: childData.requirements,
      preferred_curriculum: childData.preferredCurriculum,
      preferred_boarding: childData.preferredBoarding,
      notes: childData.notes,
    })
    .select()
    .single();
  if (error) throw new Error(error.message);
  return { success: true, child: data };
}

export async function updateChild(id: string, childData: any) {
  const { data, error } = await supabase
    .from('children')
    .update({ ...childData, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return { success: true, child: data };
}

export async function deleteChild(id: string) {
  const { error } = await supabase.from('children').delete().eq('id', id);
  if (error) throw new Error(error.message);
  return { success: true };
}

// ========== Enquiries ==========
export async function getEnquiries(status?: string) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: profile } = await supabase
    .from('profiles')
    .select('role')
    .eq('id', user.id)
    .single();

  let query = supabase
    .from('enquiries')
    .select('*, school_profiles(name, logo_url, city)');

  if (profile?.role === 'school') {
    const { data: school } = await supabase
      .from('school_profiles')
      .select('id')
      .eq('user_id', user.id)
      .single();
    if (school) query = query.eq('school_id', school.id);
  } else {
    query = query.eq('parent_id', user.id);
  }

  if (status) query = query.eq('status', status);

  const { data, error } = await query.order('sent_at', { ascending: false });
  if (error) throw new Error(error.message);
  return { success: true, enquiries: data || [] };
}

export async function createEnquiry(data: {
  school_id: string;
  child_id: string;
  message: string;
}) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: enquiry, error } = await supabase
    .from('enquiries')
    .insert({
      parent_id: user.id,
      school_id: data.school_id,
      child_ids: [data.child_id],
      requests: [],
      message: data.message,
    })
    .select()
    .single();
  if (error) throw new Error(error.message);
  return { success: true, enquiry };
}

export async function updateEnquiryStatus(id: string, status: string) {
  const updates: Record<string, any> = { status };
  if (status === 'viewed') updates.viewed_at = new Date().toISOString();
  if (status === 'responded') updates.responded_at = new Date().toISOString();

  const { error } = await supabase.from('enquiries').update(updates).eq('id', id);
  if (error) throw new Error(error.message);
  return { success: true };
}

// ========== Offers ==========
export async function getOffers(status?: string) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: profile } = await supabase
    .from('profiles')
    .select('role')
    .eq('id', user.id)
    .single();

  let query = supabase
    .from('offers')
    .select('*, school_profiles(name, logo_url), children(name)');

  if (profile?.role === 'school') {
    const { data: school } = await supabase
      .from('school_profiles')
      .select('id')
      .eq('user_id', user.id)
      .single();
    if (school) query = query.eq('school_id', school.id);
  } else {
    query = query.eq('parent_id', user.id);
  }

  if (status) query = query.eq('status', status);

  const { data, error } = await query.order('offered_at', { ascending: false });
  if (error) throw new Error(error.message);
  return { success: true, offers: data || [] };
}

export async function createOffer(enquiryId: string, fee: number, _offerType: string) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: school } = await supabase
    .from('school_profiles')
    .select('id')
    .eq('user_id', user.id)
    .single();

  const { data: enquiry } = await supabase
    .from('enquiries')
    .select('parent_id, child_ids, thread_id')
    .eq('id', enquiryId)
    .single();

  if (!school || !enquiry) throw new Error('Invalid offer request');

  const { data: offer, error } = await supabase
    .from('offers')
    .insert({
      school_id: school.id,
      parent_id: enquiry.parent_id,
      child_id: enquiry.child_ids[0],
      fee,
      academic_year: new Date().getFullYear().toString(),
      thread_id: enquiry.thread_id,
    })
    .select()
    .single();
  if (error) throw new Error(error.message);
  return { success: true, offer };
}

export async function acceptOffer(id: string) {
  const { error } = await supabase
    .from('offers')
    .update({ status: 'accepted', accepted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw new Error(error.message);
  return { success: true };
}

export async function declineOffer(id: string) {
  const { error } = await supabase
    .from('offers')
    .update({ status: 'declined', declined_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw new Error(error.message);
  return { success: true };
}

export async function confirmYear(offerId: string, year: number) {
  const { data: offer } = await supabase
    .from('offers')
    .select('confirmed_years')
    .eq('id', offerId)
    .single();

  const confirmedYears = [...(offer?.confirmed_years || []), year];

  const { error } = await supabase
    .from('offers')
    .update({
      confirmed_years: confirmedYears,
      last_confirmed_year: year,
      last_confirmed_at: new Date().toISOString(),
    })
    .eq('id', offerId);
  if (error) throw new Error(error.message);
  return { success: true };
}

// ========== Messages ==========
export async function getMessages(enquiryId: string) {
  const { data: enquiry } = await supabase
    .from('enquiries')
    .select('thread_id')
    .eq('id', enquiryId)
    .single();

  if (!enquiry) throw new Error('Enquiry not found');

  const { data, error } = await supabase
    .from('messages')
    .select('*, profiles(first_name, last_name, role)')
    .eq('thread_id', enquiry.thread_id)
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return { success: true, messages: data || [] };
}

export async function sendMessage(enquiryId: string, content: string) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const [{ data: profile }, { data: enquiry }] = await Promise.all([
    supabase.from('profiles').select('role').eq('id', user.id).single(),
    supabase.from('enquiries').select('thread_id').eq('id', enquiryId).single(),
  ]);

  if (!enquiry) throw new Error('Enquiry not found');

  const { data: message, error } = await supabase
    .from('messages')
    .insert({
      thread_id: enquiry.thread_id,
      from_user_id: user.id,
      from_role: profile?.role || 'parent',
      text: content,
    })
    .select()
    .single();
  if (error) throw new Error(error.message);
  return { success: true, message };
}

export async function markMessageRead(id: string) {
  const { error } = await supabase
    .from('messages')
    .update({ read: true, read_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw new Error(error.message);
  return { success: true };
}

export async function getUnreadCount() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { success: true, unreadCount: 0 };

  const { count, error } = await supabase
    .from('messages')
    .select('*', { count: 'exact', head: true })
    .neq('from_user_id', user.id)
    .eq('read', false);
  if (error) throw new Error(error.message);
  return { success: true, unreadCount: count || 0 };
}

// ========== Admin ==========
export async function getMetrics() {
  const [
    { count: totalSchools },
    { count: verifiedSchools },
    { count: totalParents },
    { count: activeEnquiries },
    { count: acceptedOffers },
  ] = await Promise.all([
    supabase.from('school_profiles').select('*', { count: 'exact', head: true }),
    supabase.from('school_profiles').select('*', { count: 'exact', head: true }).eq('verified', true),
    supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('role', 'parent'),
    supabase.from('enquiries').select('*', { count: 'exact', head: true }).in('status', ['sent', 'viewed']),
    supabase.from('offers').select('*', { count: 'exact', head: true }).eq('status', 'accepted'),
  ]);

  return {
    success: true,
    metrics: { totalSchools, verifiedSchools, totalParents, activeEnquiries, acceptedOffers },
  };
}

export async function getInvoices(filters?: { status?: string; schoolId?: string }) {
  let query = supabase.from('invoices').select('*, school_profiles(name)');
  if (filters?.status) query = query.eq('status', filters.status);
  if (filters?.schoolId) query = query.eq('school_id', filters.schoolId);

  const { data, error } = await query.order('issued_at', { ascending: false });
  if (error) throw new Error(error.message);
  return { success: true, invoices: data || [] };
}

export async function updateInvoiceStatus(id: string, status: string) {
  const { error } = await supabase
    .from('invoices')
    .update({ status, paid_at: status === 'paid' ? new Date().toISOString() : null })
    .eq('id', id);
  if (error) throw new Error(error.message);
  return { success: true };
}

export async function getAnalytics(period: string = '30') {
  const days = parseInt(period);
  const since = new Date();
  since.setDate(since.getDate() - days);

  const [
    { data: recentEnquiries },
    { data: recentOffers },
    { data: recentRegistrations },
  ] = await Promise.all([
    supabase.from('enquiries').select('sent_at').gte('sent_at', since.toISOString()),
    supabase.from('offers').select('offered_at, status').gte('offered_at', since.toISOString()),
    supabase.from('profiles').select('created_at').gte('created_at', since.toISOString()),
  ]);

  const accepted = recentOffers?.filter(o => o.status === 'accepted').length ?? 0;
  const total = recentOffers?.length ?? 0;

  return {
    success: true,
    analytics: {
      period: days,
      enquiries: recentEnquiries?.length ?? 0,
      offers: total,
      registrations: recentRegistrations?.length ?? 0,
      acceptanceRate: total ? Math.round((accepted / total) * 100) : 0,
    },
  };
}

export async function getAllSchools() {
  const { data, error } = await supabase
    .from('school_profiles')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return { success: true, schools: data || [] };
}

export async function getPendingSchools() {
  const { data, error } = await supabase
    .from('school_profiles')
    .select('*')
    .eq('verified', false)
    .eq('active', true)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return { success: true, schools: data || [] };
}

export async function verifySchool(id: string, notes?: string) {
  const { data: { user } } = await supabase.auth.getUser();
  const { error } = await supabase
    .from('school_profiles')
    .update({
      verified: true,
      verified_at: new Date().toISOString(),
      verified_by: user?.id,
      verification_notes: notes,
    })
    .eq('id', id);
  if (error) throw new Error(error.message);
  return { success: true };
}

export async function rejectSchool(id: string, reason: string) {
  const { error } = await supabase
    .from('school_profiles')
    .update({ active: false, verification_notes: reason })
    .eq('id', id);
  if (error) throw new Error(error.message);
  return { success: true };
}
